// Item Store Relationship main entity
export interface ItemStoreRelationship {
  id: number;                           // 主键，自增
  storeCode: string;                    // 门店代码
  ownerCode: string;                    // 商品所有者代码，默认makro
  itemCode: string;                     // 商品代码
  displayType: string;                  // 陈列方式
  status: string;                       // 商品门店状态
  toBeDetail?: number;                  // 商品是否待退市，可空 (tinyint: 0/1)
  creatorAccount: string;               // 创建人账号
  creatorName: string;                  // 创建人姓名
  gmtCreate: number;                    // 创建时间戳
  modifierAccount?: string;             // 修改人账号，可空
  modifierName?: string;                // 修改人姓名，可空
  gmtModified: number;                  // 修改时间戳
  deleted: number;                      // 逻辑删除标记 (tinyint: 0/1)
  
  // 关联数据（用于显示）
  storeName?: string;                   // 门店名称
  itemName?: string;                    // 商品名称
}

// Item entity for dropdown
export interface Item {
  code: string;                         // 商品代码
  name: string;                         // 商品名称
  ownerCode?: string;                   // 商品所有者代码
  category?: string;                    // 商品分类
  status?: string;                      // 商品状态
}

// Store entity for dropdown (using warehouse data)
export interface Store {
  code: string;                         // 门店代码
  name: string;                         // 门店名称
  type?: string;                        // 门店类型
  status?: string;                      // 门店状态
}

// Filter criteria for item store relationship list
export interface ItemStoreRelationshipFilters {
  storeCode?: string;                   // 门店代码筛选
  itemCode?: string;                    // 商品代码筛选
  status?: string;                      // 状态筛选
  ownerCode?: string;                   // 所有者代码筛选
  toBeDetail?: boolean;                  // 待退市状态筛选
  search?: string;                      // 搜索关键词（可搜索门店代码、商品代码等）
}

// Pagination parameters
export interface ItemStoreRelationshipPaginationParams {
  currentPage: number;
  pageSize: number;
  sortBy?: ItemStoreRelationshipSortField;
  sortOrder?: SortOrder;
}

// API Response wrapper following backend standards
export interface ApiResponse<T> {
  traceId: string;
  success: boolean;
  msg?: string | null;
  errorCode?: string | null;
  errorMessage: string | null;
  flag: 'success' | 'fail';
  code?: string | null;
  message?: string | null;
  data: T | null;
}

// Paginated response for item store relationship lists following backend standards
export interface ItemStoreRelationshipPaginatedResponse<T> {
  tableData: T[];
  paging: {
    currentPage: number;
    totalCount: number;
    pageSize: number;
  };
}

// Sort options for item store relationship table
export type ItemStoreRelationshipSortField =
  | 'id'
  | 'storeCode'
  | 'itemCode'
  | 'status'
  | 'ownerCode'
  | 'gmtCreate'
  | 'gmtModified'
  | 'creatorName'
  | 'modifierName';

export type SortOrder = 'asc' | 'desc';

// Table column keys for item store relationship table
export type ItemStoreRelationshipTableColumnKey =
  | 'id'
  | 'storeCode'
  | 'storeName'
  | 'itemCode'
  | 'itemName'
  | 'ownerCode'
  | 'status'
  | 'toBeDetail'
  | 'creatorName'
  | 'gmtCreate'
  | 'modifierName'
  | 'gmtModified';

// Error types for better error handling
export interface ItemStoreRelationshipErrorInfo {
  type: 'network' | 'server' | 'permission' | 'notFound' | 'validation' | 'business';
  message: string;
  code?: string;
  traceId?: string;
}

// Loading states for item store relationship management
export interface ItemStoreRelationshipLoadingState {
  list: boolean;
  storeOptions: boolean;
  itemOptions: boolean;
}

// Component props interfaces
export interface ItemStoreRelationshipFilterPanelProps {
  filters: ItemStoreRelationshipFilters;
  onFiltersChange: (filters: ItemStoreRelationshipFilters) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  storeOptions: Store[];
  itemOptions: Item[];
  onStoreSearch: (searchValue: string) => void;
  onItemSearch: (searchValue: string) => void;
}

export interface ItemStoreRelationshipTableProps {
  data: ItemStoreRelationship[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
  };
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onSortChange: (field: ItemStoreRelationshipTableColumnKey, order: SortOrder) => void;
  onRefresh: () => void;
}

export interface ItemStoreRelationshipListProps {
  // No props needed - self-contained component
}

// Status options for filter dropdown
export interface StatusOption {
  value: string;
  label: string;
  color?: string;
}

// Constants for status mapping - labels will be translated in components
export const ITEM_STORE_RELATIONSHIP_STATUS_OPTIONS: StatusOption[] = [
  { value: 'ACTIVE', label: 'itemStoreRelationship.list.status.active', color: 'green' },
  { value: 'INACTIVE', label: 'itemStoreRelationship.list.status.inactive', color: 'red' },
];

// Constants for to be detail mapping - labels will be translated in components
export const TO_BE_DETAIL_OPTIONS: StatusOption[] = [
  { value: false, label: 'itemStoreRelationship.list.toBeDetail.no', color: 'green' },
  { value: true, label: 'itemStoreRelationship.list.toBeDetail.yes', color: 'orange' }
];